/**
 * Gestor genérico de catálogo (ad_types, ad_categories) con CRUD inline.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Check } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

export interface CatalogItem {
  id: number;
  code: string;
  name: string;
  description: string | null;
  color?: string | null;
  is_active: boolean;
  sort_order: number;
}

export interface CatalogManagerProps<T extends CatalogItem> {
  title: string;
  description: string;
  queryKey: string[];
  list: () => Promise<T[]>;
  create: (body: { code: string; name: string; description?: string; color?: string; sort_order?: number }) => Promise<T>;
  patch: (id: number, body: Partial<{ code: string; name: string; description: string; color: string; is_active: boolean; sort_order: number }>) => Promise<T>;
  remove: (id: number) => Promise<void>;
  withColor?: boolean;
}

export function CatalogManager<T extends CatalogItem>(props: CatalogManagerProps<T>) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: props.queryKey, queryFn: props.list });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ code: "", name: "", description: "", color: "#2563EB" });

  const create = useMutation({
    mutationFn: (body: { code: string; name: string; description?: string; color?: string }) => props.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: props.queryKey });
      toast.success("Creado");
      reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<{ code: string; name: string; description: string; color: string; is_active: boolean; sort_order: number }> }) =>
      props.patch(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: props.queryKey });
      toast.success("Actualizado");
      reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => props.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: props.queryKey });
      toast.success("Eliminado");
    },
  });

  function reset() {
    setShowForm(false);
    setEditingId(null);
    setForm({ code: "", name: "", description: "", color: "#2563EB" });
  }

  function startEdit(item: T) {
    setEditingId(item.id);
    setForm({
      code: item.code,
      name: item.name,
      description: item.description ?? "",
      color: item.color ?? "#2563EB",
    });
    setShowForm(true);
  }

  function submit() {
    const body = {
      code: form.code,
      name: form.name,
      description: form.description || undefined,
      ...(props.withColor ? { color: form.color } : {}),
    };
    if (editingId) {
      patch.mutate({ id: editingId, body });
    } else {
      create.mutate(body);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">{props.title}</h2>
          <p className="text-xs text-ink-500">{props.description}</p>
        </div>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus size={14} /> Nuevo
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="mb-4 border-brand-200">
          <CardContent className="py-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Código *</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "_") })}
                  placeholder="slug_minusculas"
                />
                <p className="text-[10px] text-ink-400 mt-1">Solo a-z, 0-9, _ y -. Único.</p>
              </div>
              <div>
                <Label className="text-xs">Nombre visible *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              {props.withColor && (
                <div>
                  <Label className="text-xs">Color</Label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="w-12 h-10 rounded border border-ink-200"
                    />
                    <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="font-mono" />
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <Label className="text-xs">Descripción (opcional)</Label>
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={reset}>
                <X size={14} /> Cancelar
              </Button>
              <Button onClick={submit} disabled={!form.code || !form.name || create.isPending || patch.isPending}>
                <Check size={14} /> {editingId ? "Guardar" : "Crear"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {list.isLoading ? <Skeleton className="h-40" /> : (
        <div className="space-y-2">
          {(list.data ?? []).map(item => (
            <Card key={item.id} className={!item.is_active ? "opacity-50" : ""}>
              <CardContent className="py-3 flex items-center gap-3">
                {props.withColor && item.color && (
                  <div className="w-8 h-8 rounded shrink-0" style={{ backgroundColor: item.color }} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{item.name}</span>
                    <Badge variant="outline" className="font-mono text-[10px]">{item.code}</Badge>
                    {!item.is_active && <Badge variant="outline" className="text-bad">Inactivo</Badge>}
                  </div>
                  {item.description && (
                    <div className="text-xs text-ink-500 mt-0.5">{item.description}</div>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(item)} title="Editar">
                    <Pencil size={14} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(item.id)} title="Desactivar">
                    <Trash2 size={14} className="text-bad" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!(list.data ?? []).length && !list.isLoading && (
            <div className="text-center py-12 text-ink-500 border-2 border-dashed border-ink-200 rounded-lg">
              No hay registros. Crea el primero con "Nuevo".
            </div>
          )}
        </div>
      )}
    </div>
  );
}
